/**
 * i18n resources for the reservations feature slice (namespace 'reservations').
 *
 * Shipped as a self-contained bundle so the route can register its own strings
 * without touching the shared platform aggregator. Every user-facing string in
 * this slice MUST come from here — no hard-coded copy in components.
 */

/** Recursively widen string literals to `string` so each locale shares a shape. */
type Stringify<T> = { [K in keyof T]: T[K] extends string ? string : Stringify<T[K]> };

const en = {
  title: 'Reservation pipeline',
  description:
    'Voice-captured leads from inquiry to conversion. Review what was captured, fill the gaps, prepare offers and record the outcome.',
  eyebrow: 'Hospitality',
  loading: 'Loading reservations…',
  error: {
    title: 'Could not load reservations',
    description: 'Something went wrong while loading the pipeline. Please try again.',
    retry: 'Try again',
  },
  empty: {
    title: 'No reservation requests yet',
    description: 'Leads captured on calls will appear here as they come in.',
  },
  emptyFiltered: {
    title: 'No matching reservations',
    description: 'No reservations match these filters. Try clearing them.',
  },
  toolbar: {
    searchPlaceholder: 'Search id / room / board',
    statusAll: 'All statuses',
    urgencyAll: 'All urgencies',
    languagePlaceholder: 'Language',
    fromLabel: 'Arrival from',
    toLabel: 'Arrival to',
    clear: 'Clear filters',
  },
  paging: {
    prev: 'Previous',
    next: 'Next',
    perPage: 'Rows per page',
    range: '{{from}}–{{to}} of {{total}}',
  },
  pipeline: {
    countLabel: '{{count}} in this stage',
  },
  status: {
    new: 'New',
    missing_information: 'Missing information',
    offer_needed: 'Offer needed',
    offer_drafted: 'Offer drafted',
    offer_sent: 'Offer sent',
    waiting_for_guest: 'Waiting for guest',
    converted: 'Converted',
    lost: 'Lost',
    cancelled: 'Cancelled',
  },
  urgency: {
    label: 'Urgency',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  },
  card: {
    unnamedGuest: 'Unnamed guest',
    party: '{{adults}} adults · {{children}} children',
    nights: '{{count}} nights',
    openDetail: 'Open reservation',
  },
  detail: {
    title: 'Reservation detail',
    guestStay: 'Guest & stay',
    arrival: 'Arrival',
    departure: 'Departure',
    adults: 'Adults',
    children: 'Children',
    roomPreference: 'Room preference',
    boardPreference: 'Board preference',
    contactLanguage: 'Contact language',
    notProvided: 'Ask guest',
    suggestedNextAction: 'Suggested next action',
    noSuggestion: 'No suggested action.',
    missingFields: 'Missing information',
    noMissingFields: 'All information captured.',
    close: 'Close',
  },
  transition: {
    label: 'Update status',
    placeholder: 'Move to…',
    apply: 'Apply',
    applying: 'Updating…',
    none: 'No further transitions available.',
    success: 'Status updated.',
    conflict: 'That transition is no longer valid. The reservation may have changed.',
    error: 'Could not update the status. Please try again.',
  },
  offer: {
    title: 'Offer',
    none: 'No offer drafted yet.',
    create: 'Create offer draft',
    creating: 'Creating draft…',
    edit: 'Edit draft',
    status: {
      drafted: 'Drafted',
      sent: 'Sent',
      accepted: 'Accepted',
      declined: 'Declined',
      expired: 'Expired',
    },
    field: {
      totalPrice: 'Total price',
      currency: 'Currency',
      cancellationTerms: 'Cancellation terms',
      validUntil: 'Valid until',
    },
    placeholder: {
      totalPrice: 'e.g. 350.00',
      currency: 'EUR',
      cancellationTerms: 'Free cancellation up to 48h before arrival',
    },
    save: 'Save draft',
    saving: 'Saving…',
    send: 'Mark as sent',
    sending: 'Sending…',
    accept: 'Mark accepted',
    accepting: 'Updating…',
    decline: 'Mark declined',
    declining: 'Updating…',
    saved: 'Offer draft saved.',
    sent_toast: 'Offer marked as sent.',
    accepted_toast: 'Offer marked accepted.',
    declined_toast: 'Offer marked declined.',
    error: 'Could not save the offer. Please try again.',
  },
};

type Resources = Stringify<typeof en>;

const bg: Resources = {
  title: 'Поток на резервациите',
  description:
    'Заявки от обаждания — от запитване до конверсия. Прегледайте събраното, попълнете липсващото, подгответе оферти и отбележете резултата.',
  eyebrow: 'Гостоприемство',
  loading: 'Зареждане на резервациите…',
  error: {
    title: 'Резервациите не можаха да се заредят',
    description: 'Възникна грешка при зареждането на потока. Моля, опитайте отново.',
    retry: 'Опитайте отново',
  },
  empty: {
    title: 'Все още няма заявки за резервация',
    description: 'Заявките, събрани по време на обаждания, ще се появят тук.',
  },
  emptyFiltered: {
    title: 'Няма съвпадащи резервации',
    description: 'Няма резервации, отговарящи на филтрите. Опитайте да ги изчистите.',
  },
  toolbar: {
    searchPlaceholder: 'Търси id / стая / изхранване',
    statusAll: 'Всички статуси',
    urgencyAll: 'Всякаква спешност',
    languagePlaceholder: 'Език',
    fromLabel: 'Пристигане от',
    toLabel: 'Пристигане до',
    clear: 'Изчисти филтрите',
  },
  paging: {
    prev: 'Назад',
    next: 'Напред',
    perPage: 'Реда на страница',
    range: '{{from}}–{{to}} от {{total}}',
  },
  pipeline: {
    countLabel: '{{count}} на този етап',
  },
  status: {
    new: 'Нова',
    missing_information: 'Липсваща информация',
    offer_needed: 'Нужна е оферта',
    offer_drafted: 'Офертата е изготвена',
    offer_sent: 'Офертата е изпратена',
    waiting_for_guest: 'Изчаква госта',
    converted: 'Конвертирана',
    lost: 'Загубена',
    cancelled: 'Отказана',
  },
  urgency: {
    label: 'Спешност',
    low: 'Ниска',
    medium: 'Средна',
    high: 'Висока',
  },
  card: {
    unnamedGuest: 'Гост без име',
    party: '{{adults}} възрастни · {{children}} деца',
    nights: '{{count}} нощувки',
    openDetail: 'Отвори резервацията',
  },
  detail: {
    title: 'Детайли на резервацията',
    guestStay: 'Гост и престой',
    arrival: 'Пристигане',
    departure: 'Заминаване',
    adults: 'Възрастни',
    children: 'Деца',
    roomPreference: 'Предпочитана стая',
    boardPreference: 'Предпочитано изхранване',
    contactLanguage: 'Език за контакт',
    notProvided: 'Попитай госта',
    suggestedNextAction: 'Предложено следващо действие',
    noSuggestion: 'Няма предложено действие.',
    missingFields: 'Липсваща информация',
    noMissingFields: 'Цялата информация е събрана.',
    close: 'Затвори',
  },
  transition: {
    label: 'Обнови статуса',
    placeholder: 'Премести към…',
    apply: 'Приложи',
    applying: 'Обновяване…',
    none: 'Няма възможни преходи.',
    success: 'Статусът е обновен.',
    conflict: 'Този преход вече не е валиден. Резервацията може да е променена.',
    error: 'Статусът не можа да се обнови. Моля, опитайте отново.',
  },
  offer: {
    title: 'Оферта',
    none: 'Все още няма изготвена оферта.',
    create: 'Създай чернова на оферта',
    creating: 'Създаване на чернова…',
    edit: 'Редактирай черновата',
    status: {
      drafted: 'Чернова',
      sent: 'Изпратена',
      accepted: 'Приета',
      declined: 'Отказана',
      expired: 'Изтекла',
    },
    field: {
      totalPrice: 'Обща цена',
      currency: 'Валута',
      cancellationTerms: 'Условия за отказ',
      validUntil: 'Валидна до',
    },
    placeholder: {
      totalPrice: 'напр. 350.00',
      currency: 'EUR',
      cancellationTerms: 'Безплатен отказ до 48ч преди пристигане',
    },
    save: 'Запази черновата',
    saving: 'Запазване…',
    send: 'Отбележи като изпратена',
    sending: 'Изпращане…',
    accept: 'Отбележи като приета',
    accepting: 'Обновяване…',
    decline: 'Отбележи като отказана',
    declining: 'Обновяване…',
    saved: 'Черновата на офертата е запазена.',
    sent_toast: 'Офертата е отбелязана като изпратена.',
    accepted_toast: 'Офертата е отбелязана като приета.',
    declined_toast: 'Офертата е отбелязана като отказана.',
    error: 'Офертата не можа да се запази. Моля, опитайте отново.',
  },
};

const de: Resources = {
  title: 'Reservierungs-Pipeline',
  description:
    'Per Anruf erfasste Leads von der Anfrage bis zum Abschluss. Prüfen, Lücken füllen, Angebote vorbereiten und das Ergebnis festhalten.',
  eyebrow: 'Gastgewerbe',
  loading: 'Reservierungen werden geladen…',
  error: {
    title: 'Reservierungen konnten nicht geladen werden',
    description: 'Beim Laden der Pipeline ist etwas schiefgelaufen. Bitte erneut versuchen.',
    retry: 'Erneut versuchen',
  },
  empty: {
    title: 'Noch keine Reservierungsanfragen',
    description: 'In Anrufen erfasste Leads erscheinen hier, sobald sie eintreffen.',
  },
  emptyFiltered: {
    title: 'Keine passenden Reservierungen',
    description: 'Keine Reservierungen entsprechen diesen Filtern. Filter zurücksetzen.',
  },
  toolbar: {
    searchPlaceholder: 'Suche id / Zimmer / Verpflegung',
    statusAll: 'Alle Status',
    urgencyAll: 'Alle Dringlichkeiten',
    languagePlaceholder: 'Sprache',
    fromLabel: 'Anreise ab',
    toLabel: 'Anreise bis',
    clear: 'Filter zurücksetzen',
  },
  paging: {
    prev: 'Zurück',
    next: 'Weiter',
    perPage: 'Zeilen pro Seite',
    range: '{{from}}–{{to}} von {{total}}',
  },
  pipeline: {
    countLabel: '{{count}} in dieser Phase',
  },
  status: {
    new: 'Neu',
    missing_information: 'Fehlende Informationen',
    offer_needed: 'Angebot nötig',
    offer_drafted: 'Angebot entworfen',
    offer_sent: 'Angebot gesendet',
    waiting_for_guest: 'Wartet auf Gast',
    converted: 'Umgewandelt',
    lost: 'Verloren',
    cancelled: 'Storniert',
  },
  urgency: {
    label: 'Dringlichkeit',
    low: 'Niedrig',
    medium: 'Mittel',
    high: 'Hoch',
  },
  card: {
    unnamedGuest: 'Unbenannter Gast',
    party: '{{adults}} Erwachsene · {{children}} Kinder',
    nights: '{{count}} Nächte',
    openDetail: 'Reservierung öffnen',
  },
  detail: {
    title: 'Reservierungsdetails',
    guestStay: 'Gast & Aufenthalt',
    arrival: 'Anreise',
    departure: 'Abreise',
    adults: 'Erwachsene',
    children: 'Kinder',
    roomPreference: 'Zimmerwunsch',
    boardPreference: 'Verpflegungswunsch',
    contactLanguage: 'Kontaktsprache',
    notProvided: 'Gast fragen',
    suggestedNextAction: 'Empfohlene nächste Aktion',
    noSuggestion: 'Keine empfohlene Aktion.',
    missingFields: 'Fehlende Informationen',
    noMissingFields: 'Alle Informationen erfasst.',
    close: 'Schließen',
  },
  transition: {
    label: 'Status aktualisieren',
    placeholder: 'Verschieben nach…',
    apply: 'Anwenden',
    applying: 'Wird aktualisiert…',
    none: 'Keine weiteren Übergänge verfügbar.',
    success: 'Status aktualisiert.',
    conflict:
      'Dieser Übergang ist nicht mehr gültig. Die Reservierung wurde möglicherweise geändert.',
    error: 'Status konnte nicht aktualisiert werden. Bitte erneut versuchen.',
  },
  offer: {
    title: 'Angebot',
    none: 'Noch kein Angebot entworfen.',
    create: 'Angebotsentwurf erstellen',
    creating: 'Entwurf wird erstellt…',
    edit: 'Entwurf bearbeiten',
    status: {
      drafted: 'Entworfen',
      sent: 'Gesendet',
      accepted: 'Angenommen',
      declined: 'Abgelehnt',
      expired: 'Abgelaufen',
    },
    field: {
      totalPrice: 'Gesamtpreis',
      currency: 'Währung',
      cancellationTerms: 'Stornobedingungen',
      validUntil: 'Gültig bis',
    },
    placeholder: {
      totalPrice: 'z. B. 350,00',
      currency: 'EUR',
      cancellationTerms: 'Kostenlose Stornierung bis 48 Std. vor Anreise',
    },
    save: 'Entwurf speichern',
    saving: 'Wird gespeichert…',
    send: 'Als gesendet markieren',
    sending: 'Wird gesendet…',
    accept: 'Als angenommen markieren',
    accepting: 'Wird aktualisiert…',
    decline: 'Als abgelehnt markieren',
    declining: 'Wird aktualisiert…',
    saved: 'Angebotsentwurf gespeichert.',
    sent_toast: 'Angebot als gesendet markiert.',
    accepted_toast: 'Angebot als angenommen markiert.',
    declined_toast: 'Angebot als abgelehnt markiert.',
    error: 'Angebot konnte nicht gespeichert werden. Bitte erneut versuchen.',
  },
};

const fr: Resources = {
  title: 'Pipeline de réservations',
  description:
    'Pistes captées par téléphone, de la demande à la conversion. Vérifiez, complétez, préparez des offres et notez le résultat.',
  eyebrow: 'Hôtellerie',
  loading: 'Chargement des réservations…',
  error: {
    title: 'Impossible de charger les réservations',
    description: 'Une erreur est survenue lors du chargement du pipeline. Veuillez réessayer.',
    retry: 'Réessayer',
  },
  empty: {
    title: 'Aucune demande de réservation pour le moment',
    description: 'Les pistes captées lors des appels apparaîtront ici.',
  },
  emptyFiltered: {
    title: 'Aucune réservation correspondante',
    description: 'Aucune réservation ne correspond à ces filtres. Essayez de les effacer.',
  },
  toolbar: {
    searchPlaceholder: 'Rechercher id / chambre / pension',
    statusAll: 'Tous les statuts',
    urgencyAll: 'Toutes urgences',
    languagePlaceholder: 'Langue',
    fromLabel: 'Arrivée à partir du',
    toLabel: "Arrivée jusqu'au",
    clear: 'Effacer les filtres',
  },
  paging: {
    prev: 'Précédent',
    next: 'Suivant',
    perPage: 'Lignes par page',
    range: '{{from}}–{{to}} sur {{total}}',
  },
  pipeline: {
    countLabel: '{{count}} à cette étape',
  },
  status: {
    new: 'Nouvelle',
    missing_information: 'Informations manquantes',
    offer_needed: 'Offre requise',
    offer_drafted: 'Offre rédigée',
    offer_sent: 'Offre envoyée',
    waiting_for_guest: 'En attente du client',
    converted: 'Convertie',
    lost: 'Perdue',
    cancelled: 'Annulée',
  },
  urgency: {
    label: 'Urgence',
    low: 'Faible',
    medium: 'Moyenne',
    high: 'Élevée',
  },
  card: {
    unnamedGuest: 'Client sans nom',
    party: '{{adults}} adultes · {{children}} enfants',
    nights: '{{count}} nuits',
    openDetail: 'Ouvrir la réservation',
  },
  detail: {
    title: 'Détail de la réservation',
    guestStay: 'Client et séjour',
    arrival: 'Arrivée',
    departure: 'Départ',
    adults: 'Adultes',
    children: 'Enfants',
    roomPreference: 'Préférence de chambre',
    boardPreference: 'Préférence de pension',
    contactLanguage: 'Langue de contact',
    notProvided: 'Demander au client',
    suggestedNextAction: 'Action suivante suggérée',
    noSuggestion: 'Aucune action suggérée.',
    missingFields: 'Informations manquantes',
    noMissingFields: 'Toutes les informations sont saisies.',
    close: 'Fermer',
  },
  transition: {
    label: 'Mettre à jour le statut',
    placeholder: 'Déplacer vers…',
    apply: 'Appliquer',
    applying: 'Mise à jour…',
    none: 'Aucune transition disponible.',
    success: 'Statut mis à jour.',
    conflict: "Cette transition n'est plus valide. La réservation a peut-être changé.",
    error: 'Impossible de mettre à jour le statut. Veuillez réessayer.',
  },
  offer: {
    title: 'Offre',
    none: 'Aucune offre rédigée pour le moment.',
    create: 'Créer un brouillon d’offre',
    creating: 'Création du brouillon…',
    edit: 'Modifier le brouillon',
    status: {
      drafted: 'Rédigée',
      sent: 'Envoyée',
      accepted: 'Acceptée',
      declined: 'Refusée',
      expired: 'Expirée',
    },
    field: {
      totalPrice: 'Prix total',
      currency: 'Devise',
      cancellationTerms: "Conditions d'annulation",
      validUntil: "Valable jusqu'au",
    },
    placeholder: {
      totalPrice: 'p. ex. 350,00',
      currency: 'EUR',
      cancellationTerms: "Annulation gratuite jusqu'à 48 h avant l'arrivée",
    },
    save: 'Enregistrer le brouillon',
    saving: 'Enregistrement…',
    send: 'Marquer comme envoyée',
    sending: 'Envoi…',
    accept: 'Marquer comme acceptée',
    accepting: 'Mise à jour…',
    decline: 'Marquer comme refusée',
    declining: 'Mise à jour…',
    saved: "Brouillon d'offre enregistré.",
    sent_toast: 'Offre marquée comme envoyée.',
    accepted_toast: 'Offre marquée comme acceptée.',
    declined_toast: 'Offre marquée comme refusée.',
    error: "Impossible d'enregistrer l'offre. Veuillez réessayer.",
  },
};

export const namespace = 'reservations' as const;

export const resources = { en, bg, de, fr } as const;

export type ReservationsResources = Resources;
